import { create } from "zustand";
import {
  getGoogleStatus,
  triggerGoogleSync,
  disconnectGoogle,
  getSyncSources,
  triggerAllSync,
  type GoogleSyncStatus,
  type SyncSourceInfo,
} from "../lib/googleApi";

interface GoogleSyncState {
  // Google-specific
  configured: boolean;
  connected: boolean;
  lastSyncedAt: string | null;
  syncing: boolean;
  error: string | null;

  // All providers
  syncedCalendarIds: Set<string>;
  syncSources: SyncSourceInfo[];

  fetchStatus: () => Promise<void>;
  triggerSync: () => Promise<void>;
  disconnect: () => Promise<void>;
  isSyncedCalendar: (calendarId: string) => boolean;
  getSyncSourceType: (calendarId: string) => string | null;
  getSyncSourceError: (calendarId: string) => string | null;
  getFailingSyncSources: () => SyncSourceInfo[];
}

export const useGoogleSyncStore = create<GoogleSyncState>((set, get) => ({
  configured: false,
  connected: false,
  lastSyncedAt: null,
  syncing: false,
  error: null,
  syncedCalendarIds: new Set(),
  syncSources: [],

  fetchStatus: async () => {
    try {
      // Fetch Google status
      const status: GoogleSyncStatus = await getGoogleStatus();
      const googleSyncedIds = new Set(
        (status.calendars ?? []).map((c) => c.localCalendarId),
      );

      // Fetch all sync sources (ICS, CalDAV, Google)
      let allSources: SyncSourceInfo[] = [];
      try {
        const { sources } = await getSyncSources();
        allSources = sources;
      } catch {
        // Endpoint may not exist yet during migration
      }

      // Merge synced calendar IDs from all sources
      const allSyncedIds = new Set(googleSyncedIds);
      for (const source of allSources) {
        allSyncedIds.add(source.localCalendarId);
      }

      // Find most recent sync time across all sources
      let lastSyncedAt = status.lastSyncedAt ?? null;
      for (const source of allSources) {
        if (source.lastSyncedAt && (!lastSyncedAt || source.lastSyncedAt > lastSyncedAt)) {
          lastSyncedAt = source.lastSyncedAt;
        }
      }

      set({
        configured: status.configured,
        connected: status.connected,
        lastSyncedAt,
        error: status.error ?? null,
        syncedCalendarIds: allSyncedIds,
        syncSources: allSources,
      });
    } catch {
      // Silently fail — feature just won't show
    }
  },

  triggerSync: async () => {
    set({ syncing: true, error: null });
    try {
      // Trigger both Google sync and unified sync
      const promises: Promise<unknown>[] = [];
      if (get().connected) {
        promises.push(triggerGoogleSync());
      }
      promises.push(triggerAllSync());
      await Promise.all(promises);
      // Refresh status after sync
      await get().fetchStatus();
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Sync failed" });
    } finally {
      set({ syncing: false });
    }
  },

  disconnect: async () => {
    try {
      await disconnectGoogle();
      set({
        connected: false,
        lastSyncedAt: null,
        error: null,
      });
      // Re-fetch to get updated synced IDs (ICS/CalDAV still exist)
      await get().fetchStatus();
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Disconnect failed" });
    }
  },

  isSyncedCalendar: (calendarId: string) => {
    return get().syncedCalendarIds.has(calendarId);
  },

  getSyncSourceType: (calendarId: string) => {
    const source = get().syncSources.find((s) => s.localCalendarId === calendarId);
    return source?.type ?? null;
  },

  getSyncSourceError: (calendarId: string) => {
    const source = get().syncSources.find((s) => s.localCalendarId === calendarId);
    return source?.lastError ?? null;
  },

  getFailingSyncSources: () => {
    return get().syncSources.filter((s) => s.lastError);
  },
}));
